import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Flag, MessageSquareHeart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import {
  getQuestionReports,
  getStudentFeedbackAdmin,
  updateQuestionReport,
  updateStudentFeedbackAdmin,
} from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const formatDate = (value) => {
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    return value;
  }
};

const StatusSelect = ({ value, options, onChange, disabled }) => (
  <select
    value={value || "new"}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    className="h-9 rounded-lg border border-[color:rgba(10,27,51,0.18)] bg-white px-3 text-xs font-semibold text-[var(--ibp-ink)] disabled:opacity-50"
  >
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
  </select>
);

function QualityInbox() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("reports");
  const [busyId, setBusyId] = useState("");
  const reports = useQuery({ queryKey: ["quality-question-reports"], queryFn: getQuestionReports });
  const feedback = useQuery({ queryKey: ["quality-feedback"], queryFn: getStudentFeedbackAdmin });

  const updateReport = async (id, status) => {
    setBusyId(id);
    try {
      await updateQuestionReport(id, status);
      await queryClient.invalidateQueries({ queryKey: ["quality-question-reports"] });
    } catch (error) {
      toast.error("Could not update report", { description: error?.response?.data?.detail || "" });
    } finally {
      setBusyId("");
    }
  };

  const updateFeedback = async (id, status) => {
    setBusyId(id);
    try {
      await updateStudentFeedbackAdmin(id, status);
      await queryClient.invalidateQueries({ queryKey: ["quality-feedback"] });
      if (status === "approved") toast.success("Approved for the storefront");
    } catch (error) {
      toast.error("Could not update feedback", { description: error?.response?.data?.detail || "" });
    } finally {
      setBusyId("");
    }
  };

  const reportItems = reports.data || [];
  const feedbackItems = feedback.data || [];
  const loading = tab === "reports" ? reports.isLoading : feedback.isLoading;

  return (
    <div>
      <div className="inline-flex rounded-xl border border-[color:rgba(10,27,51,0.14)] bg-white p-1">
        <button
          type="button"
          onClick={() => setTab("reports")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === "reports" ? "bg-[var(--ibp-navy-950)] text-white" : "text-[var(--ibp-ink)]"}`}
        >
          Question reports ({reportItems.filter((item) => item.status === "new").length})
        </button>
        <button
          type="button"
          onClick={() => setTab("feedback")}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === "feedback" ? "bg-[var(--ibp-navy-950)] text-white" : "text-[var(--ibp-ink)]"}`}
        >
          Student feedback ({feedbackItems.filter((item) => item.status === "new").length})
        </button>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.62)]">Loading...</p>
      ) : tab === "reports" ? (
        <div className="mt-6 space-y-4">
          {!reportItems.length ? (
            <p className="border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-12 text-center text-sm text-[color:rgba(11,18,32,0.62)]">
              No question reports yet.
            </p>
          ) : (
            reportItems.map((item) => (
              <article
                key={item.id}
                data-testid={STUDY.adminQuestionReportRow}
                className="border border-[color:rgba(10,27,51,0.12)] bg-white p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
                      <Flag className="h-4 w-4 text-[#B7791F]" /> {item.question_id} - {item.reason?.replaceAll("_", " ")}
                    </p>
                    <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.52)]">
                      {formatDate(item.created_at)} - {item.customer_email || "No customer email"}
                    </p>
                  </div>
                  <StatusSelect
                    value={item.status}
                    options={["new", "reviewed", "resolved", "archived"]}
                    disabled={busyId === item.id}
                    onChange={(status) => updateReport(item.id, status)}
                  />
                </div>
                <p className="mt-3 text-sm font-medium leading-6 text-[var(--ibp-ink)]">{item.question_text}</p>
                {item.details ? (
                  <p className="mt-3 border-l-4 border-l-[var(--ibp-gold-500)] pl-3 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
                    {item.details}
                  </p>
                ) : null}
                {item.source_reference ? (
                  <p className="mt-3 text-xs text-[color:rgba(11,18,32,0.52)]">Source: {item.source_reference}</p>
                ) : null}
              </article>
            ))
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {!feedbackItems.length ? (
            <p className="border border-dashed border-[color:rgba(10,27,51,0.2)] bg-white px-6 py-12 text-center text-sm text-[color:rgba(11,18,32,0.62)]">
              No student feedback yet.
            </p>
          ) : (
            feedbackItems.map((item) => (
              <article
                key={item.id}
                data-testid={STUDY.adminFeedbackRow}
                className="border border-[color:rgba(10,27,51,0.12)] bg-white p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
                      <MessageSquareHeart className="h-4 w-4 text-[#B7791F]" /> {item.public_attribution || "Illinois broker candidate"}
                    </p>
                    <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.52)]">
                      {formatDate(item.created_at)} - {item.customer_email || "No customer email"}
                    </p>
                  </div>
                  <StatusSelect
                    value={item.status}
                    options={item.permission_to_quote ? ["new", "reviewed", "approved", "archived"] : ["new", "reviewed", "archived"]}
                    disabled={busyId === item.id}
                    onChange={(status) => updateFeedback(item.id, status)}
                  />
                </div>
                <p className="mt-4 text-sm leading-7 text-[var(--ibp-ink)]">&ldquo;{item.helpful}&rdquo;</p>
                {item.improve ? (
                  <p className="mt-3 text-sm leading-6 text-[color:rgba(11,18,32,0.68)]">
                    <strong>Improve:</strong> {item.improve}
                  </p>
                ) : null}
                <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--ibp-offwhite)] px-3 py-1.5 text-[color:rgba(11,18,32,0.68)]">
                    <ShieldCheck className="h-3.5 w-3.5" /> {item.permission_to_quote ? "Quote permitted" : "Private only"}
                  </span>
                  {item.passed_exam === true ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[color:rgba(21,128,61,0.10)] px-3 py-1.5 text-[#15803d]">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Customer reported passing
                    </span>
                  ) : null}
                </div>
              </article>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminQuality() {
  return (
    <div data-testid={STUDY.adminQualityPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="Quality inbox"
            subtitle="Review customer-reported question issues and publish only permissioned, verified feedback."
          />
        </div>
      </section>
      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <AdminGate>
            <QualityInbox />
          </AdminGate>
        </div>
      </section>
    </div>
  );
}
